import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Loader2, Plus, Settings2 } from 'lucide-react';

import { adminDataApi, InvoiceProcessingSettings, LlmConfig, LlmProfile, WorkerRuntimeSettings } from '../../api/adminDataApi';
import { formatLocalTimestamp } from '../../utils/formatLocalTimestamp';

type ConfigTab = 'invoice' | 'rateLimits' | 'llm';

const tabs: Array<{ id: ConfigTab; label: string }> = [
    { id: 'invoice', label: 'Invoice Image Processing' },
    { id: 'rateLimits', label: 'Rate Limits & Backoff' },
    { id: 'llm', label: 'LLM & System Configuration' },
];

const defaultInvoiceSettings: InvoiceProcessingSettings = {
    image_text_provider: 'ocr',
    vision_provider: 'gemini',
    vision_model: '',
    vision_max_tokens: 8192,
};

const defaultWorkerRuntime: WorkerRuntimeSettings = {
    vertex_input_tpm_limit: 800000,
    gemini_input_tpm_limit: 800000,
    llm_multimodal_image_token_estimate: 8000,
    vendor_resolution_llm_request_delay_seconds: 1,
    vendor_resolution_inter_batch_delay_seconds: 2,
    vendor_resolution_llm_max_retries: 3,
    vendor_resolution_llm_backoff_base_seconds: 1,
    vendor_resolution_llm_backoff_max_seconds: 8,
    invoice_llm_request_delay_seconds: 1,
    invoice_llm_max_retries: 3,
    invoice_llm_backoff_base_seconds: 1,
    invoice_llm_backoff_max_seconds: 8,
};

const tabDescriptions: Record<ConfigTab, string> = {
    invoice:
        'Control how new JPG and PNG invoice uploads are transcribed before parsing. New jobs pick up the saved mode after the worker cache refreshes.',
    rateLimits:
        'Control Faust-side TPM throttling and retry behavior for onboarding vendor resolution and invoice OCR.',
    llm:
        'Manage global LLM defaults and feature-specific model overrides. These settings impact logic routing and generation quality.',
};

const fieldClass =
    'h-11 w-full rounded-lg border border-gray-200 bg-white px-4 text-sm text-gray-900 shadow-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400';

const SelectShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="relative">
        {children}
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
    </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode; modified?: boolean; onRestore?: () => void; className?: string }> = ({
    label,
    children,
    modified,
    onRestore,
    className = '',
}) => (
    <label className={`block ${className}`}>
        <div className="mb-2 flex min-h-5 items-center justify-between gap-3">
            <span className="text-sm font-semibold text-gray-500">{label}</span>
            {modified ? (
                <button type="button" onClick={onRestore} className="text-xs font-medium text-indigo-500 hover:text-indigo-700">
                    Restore
                </button>
            ) : null}
        </div>
        {children}
    </label>
);

const FooterActions: React.FC<{
    updatedAt?: string | null;
    updatedBy?: string | null;
    hasChanges: boolean;
    isSaving: boolean;
    isLoading?: boolean;
    onReset: () => void;
    onSave: () => void;
}> = ({ updatedAt, updatedBy, hasChanges, isSaving, isLoading, onReset, onSave }) => (
    <div className="mt-9 flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-gray-500">
            {updatedAt ? `Last updated: ${formatLocalTimestamp(updatedAt)}${updatedBy ? ` by ${updatedBy}` : ''}` : 'Last updated: -'}
        </div>
        <div className="flex items-center gap-5">
            <button type="button" onClick={onReset} disabled={!hasChanges || isSaving} className="text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:text-gray-300">
                Reset
            </button>
            <button
                type="button"
                onClick={onSave}
                disabled={!hasChanges || isSaving || isLoading}
                className="inline-flex h-12 min-w-28 items-center justify-center rounded-lg bg-gray-900 px-8 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
                {isSaving || isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </button>
        </div>
    </div>
);

const AdminConfigPage: React.FC = () => {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<ConfigTab>('invoice');
    const [invoiceSettingsDraft, setInvoiceSettingsDraft] = useState<InvoiceProcessingSettings | null>(null);
    const [workerRuntimeDraft, setWorkerRuntimeDraft] = useState<WorkerRuntimeSettings | null>(null);
    const [llmConfigDraft, setLlmConfigDraft] = useState<Partial<LlmConfig['effective']> | null>(null);

    const invoiceSettingsQuery = useQuery({
        queryKey: ['admin-invoice-processing-settings'],
        queryFn: () => adminDataApi.getInvoiceProcessingSettings(),
    });

    const workerRuntimeQuery = useQuery({
        queryKey: ['admin-worker-runtime-settings'],
        queryFn: () => adminDataApi.getWorkerRuntimeSettings(),
    });

    const llmConfigQuery = useQuery({
        queryKey: ['admin-llm-config'],
        queryFn: () => adminDataApi.getLlmConfig(),
    });

    const invoiceSettingsMutation = useMutation({
        mutationFn: (payload: InvoiceProcessingSettings) => adminDataApi.updateInvoiceProcessingSettings(payload),
        onSuccess: (data) => {
            setInvoiceSettingsDraft(null);
            queryClient.setQueryData(['admin-invoice-processing-settings'], data);
            queryClient.invalidateQueries({ queryKey: ['admin-invoice-processing-settings'] });
        },
    });

    const workerRuntimeMutation = useMutation({
        mutationFn: (payload: WorkerRuntimeSettings) => adminDataApi.updateWorkerRuntimeSettings(payload),
        onSuccess: (data) => {
            setWorkerRuntimeDraft(null);
            queryClient.setQueryData(['admin-worker-runtime-settings'], data);
            queryClient.invalidateQueries({ queryKey: ['admin-worker-runtime-settings'] });
        },
    });

    const llmConfigMutation = useMutation({
        mutationFn: (payload: Partial<LlmConfig['effective']>) => adminDataApi.updateLlmConfig(payload),
        onSuccess: () => {
            setLlmConfigDraft(null);
            queryClient.invalidateQueries({ queryKey: ['admin-llm-config'] });
        },
    });

    const refreshAll = () => {
        invoiceSettingsQuery.refetch();
        workerRuntimeQuery.refetch();
        llmConfigQuery.refetch();
    };

    const currentInvoiceSettings = invoiceSettingsDraft || invoiceSettingsQuery.data || defaultInvoiceSettings;
    const currentWorkerRuntime = workerRuntimeDraft || workerRuntimeQuery.data || defaultWorkerRuntime;

    const setInvoiceField = <K extends keyof InvoiceProcessingSettings>(key: K, value: InvoiceProcessingSettings[K]) => {
        setInvoiceSettingsDraft((previous) => ({
            ...defaultInvoiceSettings,
            ...(invoiceSettingsQuery.data || {}),
            ...(previous || {}),
            [key]: value,
        }));
    };

    const setWorkerRuntimeField = <K extends keyof WorkerRuntimeSettings>(key: K, value: WorkerRuntimeSettings[K]) => {
        setWorkerRuntimeDraft((previous) => ({
            ...defaultWorkerRuntime,
            ...(workerRuntimeQuery.data || {}),
            ...(previous || {}),
            [key]: value,
        }));
    };

    const setLlmField = (key: keyof LlmConfig['effective'], value: any) => {
        setLlmConfigDraft((previous) => ({
            ...(previous || {}),
            [key]: value,
        }));
    };

    const setLlmProfileField = (profileKey: string, field: keyof LlmProfile, value: any) => {
        setLlmConfigDraft((previous) => {
            const draft = previous || {};
            const profiles = { ...(draft.profiles || {}) };
            profiles[profileKey] = {
                ...(profiles[profileKey] || {}),
                [field]: value,
            };
            return { ...draft, profiles };
        });
    };

    const getLlmValue = <K extends keyof LlmConfig['effective']>(key: K): LlmConfig['effective'][K] | undefined => {
        if (llmConfigDraft && key in llmConfigDraft) return llmConfigDraft[key];
        return llmConfigQuery.data?.effective?.[key];
    };

    const isLlmOverridden = (key: string, profileKey?: string) => {
        if (profileKey) {
            const draftProfile = llmConfigDraft?.profiles?.[profileKey];
            const backendOverride = llmConfigQuery.data?.overrides?.profiles?.[profileKey];
            if (draftProfile && key in draftProfile) return draftProfile[key as keyof LlmProfile] !== null;
            return Boolean(backendOverride && key in backendOverride);
        }
        if (llmConfigDraft && key in llmConfigDraft) return llmConfigDraft[key as keyof LlmConfig['effective']] !== null;
        return (llmConfigQuery.data?.overrides as any)?.[key] !== undefined;
    };

    const restoreLlmField = (key: keyof LlmConfig['effective']) => setLlmField(key, null);
    const restoreLlmProfileField = (profileKey: string, field: keyof LlmProfile) => setLlmProfileField(profileKey, field, null);

    const addLlmProfile = () => {
        const standardProfiles = ['classifier', 'response_simple', 'response_deep', 'signal_narrator', 'panel_insights', 'memory_summary'];
        const existing = Object.keys(llmConfigQuery.data?.effective?.profiles || {});
        const suggestions = standardProfiles.filter((name) => !existing.includes(name));
        const name = window.prompt(`Enter unique profile name:${suggestions.length ? `\nSuggestions: ${suggestions.join(', ')}` : ''}`);
        if (!name) return;
        setLlmProfileField(name, 'model', '');
    };

    const renderInvoiceTab = () => (
        <>
            {invoiceSettingsQuery.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Failed to load invoice processing settings.</div>
            ) : (
                <div className="grid gap-7 md:grid-cols-2">
                    <Field label="Image Text Provider">
                        <SelectShell>
                            <select
                                value={currentInvoiceSettings.image_text_provider || 'ocr'}
                                onChange={(event) => setInvoiceField('image_text_provider', event.target.value as InvoiceProcessingSettings['image_text_provider'])}
                                className={`${fieldClass} appearance-none pr-10`}
                            >
                                <option value="ocr">OCR only</option>
                                <option value="llm">Vision provider default</option>
                                <option value="llm_text">Vision text only</option>
                                <option value="llm_json">Structured vision JSON</option>
                                <option value="hybrid">Hybrid</option>
                            </select>
                        </SelectShell>
                    </Field>
                    <Field label="Vision Provider">
                        <SelectShell>
                            <select
                                value={currentInvoiceSettings.vision_provider || 'gemini'}
                                onChange={(event) => setInvoiceField('vision_provider', event.target.value as InvoiceProcessingSettings['vision_provider'])}
                                className={`${fieldClass} appearance-none pr-10`}
                            >
                                <option value="gemini">Gemini</option>
                                <option value="vertex">Vertex</option>
                                <option value="openai">OpenAI</option>
                            </select>
                        </SelectShell>
                    </Field>
                    <Field label="Vision Model">
                        <input
                            value={currentInvoiceSettings.vision_model || ''}
                            onChange={(event) => setInvoiceField('vision_model', event.target.value)}
                            placeholder="Default to provider"
                            className={fieldClass}
                        />
                    </Field>
                    <Field label="Vision Max Tokens">
                        <input
                            type="number"
                            min={512}
                            max={32768}
                            value={currentInvoiceSettings.vision_max_tokens || 8192}
                            onChange={(event) => setInvoiceField('vision_max_tokens', Number(event.target.value))}
                            className={fieldClass}
                        />
                    </Field>
                </div>
            )}
            <FooterActions
                updatedAt={invoiceSettingsQuery.data?.updated_at}
                updatedBy={invoiceSettingsQuery.data?.updated_by}
                hasChanges={Boolean(invoiceSettingsDraft)}
                isSaving={invoiceSettingsMutation.isPending}
                isLoading={invoiceSettingsQuery.isLoading}
                onReset={() => setInvoiceSettingsDraft(null)}
                onSave={() => invoiceSettingsMutation.mutate(currentInvoiceSettings)}
            />
        </>
    );

    const renderRateLimitTab = () => (
        <>
            {workerRuntimeQuery.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Failed to load worker runtime settings.</div>
            ) : (
                <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-3">
                    <Field label="Vertex Input TPM">
                        <input type="number" min={0} value={currentWorkerRuntime.vertex_input_tpm_limit} onChange={(event) => setWorkerRuntimeField('vertex_input_tpm_limit', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Gemini Input TPM">
                        <input type="number" min={0} value={currentWorkerRuntime.gemini_input_tpm_limit} onChange={(event) => setWorkerRuntimeField('gemini_input_tpm_limit', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Image Token Estimate">
                        <input type="number" min={100} value={currentWorkerRuntime.llm_multimodal_image_token_estimate} onChange={(event) => setWorkerRuntimeField('llm_multimodal_image_token_estimate', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Vendor Request Delay (s)">
                        <input type="number" step="0.1" min={0} value={currentWorkerRuntime.vendor_resolution_llm_request_delay_seconds} onChange={(event) => setWorkerRuntimeField('vendor_resolution_llm_request_delay_seconds', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Vendor Batch Delay (s)">
                        <input type="number" step="0.1" min={0} value={currentWorkerRuntime.vendor_resolution_inter_batch_delay_seconds} onChange={(event) => setWorkerRuntimeField('vendor_resolution_inter_batch_delay_seconds', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Invoice Request Delay (s)">
                        <input type="number" step="0.1" min={0} value={currentWorkerRuntime.invoice_llm_request_delay_seconds} onChange={(event) => setWorkerRuntimeField('invoice_llm_request_delay_seconds', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Vendor Max Retries">
                        <input type="number" min={1} value={currentWorkerRuntime.vendor_resolution_llm_max_retries} onChange={(event) => setWorkerRuntimeField('vendor_resolution_llm_max_retries', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Vendor Backoff Base (s)">
                        <input type="number" step="0.1" min={0.1} value={currentWorkerRuntime.vendor_resolution_llm_backoff_base_seconds} onChange={(event) => setWorkerRuntimeField('vendor_resolution_llm_backoff_base_seconds', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Vendor Backoff Max (s)">
                        <input type="number" step="0.1" min={0.1} value={currentWorkerRuntime.vendor_resolution_llm_backoff_max_seconds} onChange={(event) => setWorkerRuntimeField('vendor_resolution_llm_backoff_max_seconds', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Invoice Max Retries">
                        <input type="number" min={1} value={currentWorkerRuntime.invoice_llm_max_retries} onChange={(event) => setWorkerRuntimeField('invoice_llm_max_retries', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Invoice Backoff Base (s)">
                        <input type="number" step="0.1" min={0.1} value={currentWorkerRuntime.invoice_llm_backoff_base_seconds} onChange={(event) => setWorkerRuntimeField('invoice_llm_backoff_base_seconds', Number(event.target.value))} className={fieldClass} />
                    </Field>
                    <Field label="Invoice Backoff Max (s)">
                        <input type="number" step="0.1" min={0.1} value={currentWorkerRuntime.invoice_llm_backoff_max_seconds} onChange={(event) => setWorkerRuntimeField('invoice_llm_backoff_max_seconds', Number(event.target.value))} className={fieldClass} />
                    </Field>
                </div>
            )}
            <FooterActions
                updatedAt={workerRuntimeQuery.data?.updated_at}
                updatedBy={workerRuntimeQuery.data?.updated_by}
                hasChanges={Boolean(workerRuntimeDraft)}
                isSaving={workerRuntimeMutation.isPending}
                isLoading={workerRuntimeQuery.isLoading}
                onReset={() => setWorkerRuntimeDraft(null)}
                onSave={() => workerRuntimeMutation.mutate(currentWorkerRuntime)}
            />
        </>
    );

    const renderLlmTab = () => (
        <>
            {llmConfigQuery.isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Failed to load LLM configurations.</div>
            ) : (
                <div className="space-y-8">
                    <section>
                        <h2 className="mb-4 text-sm font-semibold text-gray-900">Global Defaults</h2>
                        <div className="grid gap-7 md:grid-cols-2">
                            <Field label="Default Max Tokens" modified={isLlmOverridden('default_max_tokens')} onRestore={() => restoreLlmField('default_max_tokens')}>
                                <input type="number" value={getLlmValue('default_max_tokens') ?? 4096} onChange={(event) => setLlmField('default_max_tokens', Number(event.target.value))} className={fieldClass} />
                            </Field>
                            <Field label="Default Temperature" modified={isLlmOverridden('default_temperature')} onRestore={() => restoreLlmField('default_temperature')}>
                                <input type="number" step="0.1" min={0} max={2} value={getLlmValue('default_temperature') ?? 0.7} onChange={(event) => setLlmField('default_temperature', Number(event.target.value))} className={fieldClass} />
                            </Field>
                        </div>
                    </section>

                    <section>
                        <h2 className="mb-4 text-sm font-semibold text-gray-900">System Overrides</h2>
                        <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-3">
                            <Field label="QBO Mapping Max Tokens" modified={isLlmOverridden('qbo_mapping_max_tokens')} onRestore={() => restoreLlmField('qbo_mapping_max_tokens')}>
                                <input type="number" value={getLlmValue('qbo_mapping_max_tokens') ?? ''} onChange={(event) => setLlmField('qbo_mapping_max_tokens', event.target.value === '' ? null : Number(event.target.value))} placeholder="Default" className={fieldClass} />
                            </Field>
                            <Field label="QBO Mapping Temperature" modified={isLlmOverridden('qbo_mapping_temperature')} onRestore={() => restoreLlmField('qbo_mapping_temperature')}>
                                <input type="number" step="0.1" min={0} max={2} value={getLlmValue('qbo_mapping_temperature') ?? ''} onChange={(event) => setLlmField('qbo_mapping_temperature', event.target.value === '' ? null : Number(event.target.value))} placeholder="Default" className={fieldClass} />
                            </Field>
                            <Field label="Invoice Vision Model" modified={isLlmOverridden('invoice_vision_model')} onRestore={() => restoreLlmField('invoice_vision_model')}>
                                <input value={getLlmValue('invoice_vision_model') ?? ''} onChange={(event) => setLlmField('invoice_vision_model', event.target.value)} placeholder="Default" className={fieldClass} />
                            </Field>
                            <Field label="Invoice Vision Tokens" modified={isLlmOverridden('invoice_vision_max_tokens')} onRestore={() => restoreLlmField('invoice_vision_max_tokens')}>
                                <input type="number" value={getLlmValue('invoice_vision_max_tokens') ?? 8192} onChange={(event) => setLlmField('invoice_vision_max_tokens', Number(event.target.value))} className={fieldClass} />
                            </Field>
                            <Field label="Invoice Extraction Tokens" modified={isLlmOverridden('invoice_extraction_max_tokens')} onRestore={() => restoreLlmField('invoice_extraction_max_tokens')}>
                                <input type="number" value={getLlmValue('invoice_extraction_max_tokens') ?? 16384} onChange={(event) => setLlmField('invoice_extraction_max_tokens', Number(event.target.value))} className={fieldClass} />
                            </Field>
                        </div>
                    </section>

                    <section>
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-gray-900">Profile Overrides</h2>
                            <button type="button" onClick={addLlmProfile} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:border-indigo-300 hover:text-indigo-600">
                                <Plus className="h-4 w-4" />
                                Add Profile
                            </button>
                        </div>
                        <div className="grid gap-4 xl:grid-cols-2">
                            {Object.entries(llmConfigQuery.data?.effective?.profiles || {}).map(([key, effectiveProfile]) => {
                                const getProfileValue = (field: keyof LlmProfile) => {
                                    const draft = llmConfigDraft?.profiles?.[key]?.[field];
                                    return draft !== undefined ? draft : effectiveProfile[field];
                                };

                                return (
                                    <div key={key} className="rounded-lg border border-gray-200 bg-white p-4">
                                        <div className="mb-4 flex items-center gap-2">
                                            <Settings2 className="h-4 w-4 text-indigo-500" />
                                            <h3 className="text-sm font-semibold text-gray-900">{key}</h3>
                                        </div>
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <Field label="Provider" modified={isLlmOverridden('provider', key)} onRestore={() => restoreLlmProfileField(key, 'provider')}>
                                                <SelectShell>
                                                    <select value={(getProfileValue('provider') as string) ?? ''} onChange={(event) => setLlmProfileField(key, 'provider', event.target.value)} className={`${fieldClass} appearance-none pr-10`}>
                                                        <option value="">Default</option>
                                                        <option value="gemini">Gemini</option>
                                                        <option value="vertex">Vertex</option>
                                                        <option value="openai">OpenAI</option>
                                                    </select>
                                                </SelectShell>
                                            </Field>
                                            <Field label="Model" modified={isLlmOverridden('model', key)} onRestore={() => restoreLlmProfileField(key, 'model')}>
                                                <input value={(getProfileValue('model') as string) || ''} onChange={(event) => setLlmProfileField(key, 'model', event.target.value)} placeholder="Default" className={fieldClass} />
                                            </Field>
                                            <Field label="Temperature" modified={isLlmOverridden('temperature', key)} onRestore={() => restoreLlmProfileField(key, 'temperature')}>
                                                <input type="number" step="0.1" value={(getProfileValue('temperature') as number) ?? ''} onChange={(event) => setLlmProfileField(key, 'temperature', event.target.value === '' ? null : Number(event.target.value))} placeholder="Default" className={fieldClass} />
                                            </Field>
                                            <Field label="Max Tokens" modified={isLlmOverridden('max_tokens', key)} onRestore={() => restoreLlmProfileField(key, 'max_tokens')}>
                                                <input type="number" value={(getProfileValue('max_tokens') as number) ?? ''} onChange={(event) => setLlmProfileField(key, 'max_tokens', event.target.value === '' ? null : Number(event.target.value))} placeholder="Default" className={fieldClass} />
                                            </Field>
                                            <Field label="Thinking Tokens" modified={isLlmOverridden('thinking_tokens', key)} onRestore={() => restoreLlmProfileField(key, 'thinking_tokens')}>
                                                <input type="number" value={(getProfileValue('thinking_tokens') as number) ?? ''} onChange={(event) => setLlmProfileField(key, 'thinking_tokens', event.target.value === '' ? null : Number(event.target.value))} placeholder="None" className={fieldClass} />
                                            </Field>
                                            <div className="flex h-full min-h-[73px] items-end">
                                                <label className="flex h-11 w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600">
                                                    <span>Stream</span>
                                                    <input type="checkbox" checked={!!getProfileValue('stream')} onChange={(event) => setLlmProfileField(key, 'stream', event.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </div>
            )}
            <FooterActions
                updatedAt={llmConfigQuery.data?.last_updated}
                hasChanges={Boolean(llmConfigDraft)}
                isSaving={llmConfigMutation.isPending}
                isLoading={llmConfigQuery.isLoading}
                onReset={() => setLlmConfigDraft(null)}
                onSave={() => llmConfigDraft && llmConfigMutation.mutate(llmConfigDraft)}
            />
        </>
    );

    return (
        <div className="flex h-full flex-col gap-8 bg-white px-6 py-6">
            <div className="flex items-start justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Configuration</h1>
                <button
                    type="button"
                    onClick={refreshAll}
                    className="h-12 rounded-lg bg-indigo-500 px-8 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
                >
                    Refresh
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-8">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`border-b-2 pb-3 text-sm font-semibold transition-colors ${
                            activeTab === tab.id
                                ? 'border-indigo-500 text-gray-900'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-900'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="w-full max-w-5xl rounded-3xl bg-gray-50 px-7 py-7">
                <p className="mb-8 max-w-4xl text-base leading-7 text-gray-600">{tabDescriptions[activeTab]}</p>
                {activeTab === 'invoice' && renderInvoiceTab()}
                {activeTab === 'rateLimits' && renderRateLimitTab()}
                {activeTab === 'llm' && renderLlmTab()}
            </div>
        </div>
    );
};

export default AdminConfigPage;
